import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelectedOrganizationName } from '@client/store/organization/organization.hook';
import { requireCreatedProjectDesignPath } from '@shared/helpers/router.helpers';
import { useLocalStorage } from 'usehooks-ts';
import { Box, Button, IconButton, Portal, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import type { FfuImportProgress } from '@shared/helpers/ffu-import-progress.helpers';
import { useProjectState } from '@client/store/project';
import {
  collectProjectFilesFromDataTransfer,
  collectProjectFilesFromFileList,
  type ProjectFileClientUpload,
} from '@client/helpers/project-file-upload-normalize.helpers';
import { DropZoneOverlay } from '@features/csv-import/DropZoneOverlay';
import {
  ChatWindow,
  ChatWindowHeader,
  ChatWindowContent,
} from '@ui/ChatWindow';
import { aiChatApi } from '@features/ai-chat/ai-chat.api';
import { ChatMarkdown } from '@features/ai-chat/ChatMarkdown';
import { AgentChatMessage } from '@features/ai-chat/AgentChatMessage';
import {
  formatChatThreadTimestamp,
  shouldShowChatTimestamp,
} from '@features/ai-chat/ai-chat-timestamp.helpers';
import { ffuImportApi } from './ffu-import.api';
import { useFfuFileDrop } from './use-ffu-file-drop.hook';

const POLL_MS = 3000;
const IDLE_POLL_MS = 30000;
const CHAT_WIDTH = 440;
const CHAT_HEIGHT = 680;
const DEFAULT_CHAT_SIZE = { width: CHAT_WIDTH, height: CHAT_HEIGHT };
const CHAT_SIZE_STORAGE_KEY = 'nodon:ffu-import:chat:size';
const useStyles = makeStyles()((theme) => ({
  chat: { zIndex: theme.zIndex.modal },
  title: {
    fontWeight: 600,
    fontSize: 11,
    color: 'rgba(0,0,0,0.8)',
    letterSpacing: '0.44px',
  },
  close: { opacity: 0.4, padding: 4 },
  status: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '12px 20px',
    flexShrink: 0,
  },
  statusTitle: { fontSize: 13, fontWeight: 600, lineHeight: '20px' },
  detail: {
    whiteSpace: 'pre-line',
    fontSize: 13,
    lineHeight: '20px',
    color: 'rgba(0,0,0,0.55)',
  },
  summary: {
    fontSize: 13,
    lineHeight: '20px',
    color: 'rgba(0,0,0,0.8)',
    overflowWrap: 'anywhere',
  },
  actions: { display: 'flex', gap: 16, paddingBlock: 16 },
  drop: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 12,
    border: '1px dashed rgba(0,0,0,0.15)',
    borderRadius: 8,
    cursor: 'pointer',
  },
}));

const useFfuImports = () => {
  const [imports, setImports] = useState<FfuImportProgress[]>([]);
  const refresh = useCallback(async () => {
    const rows = await ffuImportApi.list().catch(() => null);
    if (rows !== null) setImports(rows);
  }, []);
  const running = imports.some((row) => !row.failed && row.projectId === null);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    const timer = window.setInterval(
      () => void refresh(),
      running ? POLL_MS : IDLE_POLL_MS,
    );
    return () => window.clearInterval(timer);
  }, [refresh, running]);
  return { imports, refresh };
};

/** Minutes and seconds since a moment. */
const useElapsed = (since: string): string => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.floor((now - Date.parse(since)) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

/** Why an upload did not reach the server, and whether to offer a retry. */
export type UploadFailure = { text: string; retryable: boolean };

const uploadFailureOf = (reason: unknown): UploadFailure => {
  // No answer or a server error: the network or the server, not the files.
  const status = axios.isAxiosError(reason) ? reason.response?.status : 0;
  if (status === undefined || status === 0 || status >= 500) {
    return {
      text: 'The upload did not go through. Try again.',
      retryable: true,
    };
  }
  return {
    text:
      status === 413
        ? 'These files are too large to upload.'
        : 'These files could not be uploaded.',
    retryable: false,
  };
};

type ImportCardProps = {
  row: FfuImportProgress | null;
  fileCount?: number;
  startedAt?: string;
  uploadFailure?: UploadFailure | null;
  busy?: boolean;
  onRetry?: () => void;
  onDiscard?: () => void;
  onOpen?: () => void;
};

/** Reuse the saved opening message; reading the list never creates a new chat. */
const FfuProjectSummary: React.FC<{ projectId: number }> = ({ projectId }) => {
  const { classes } = useStyles();
  const [summary, setSummary] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setSummary(null);
    setFailed(false);
    const load = async () => {
      const conversations = await aiChatApi.listConversations(projectId);
      // The FFU creates the project's first conversation before attaching the batch.
      const first = [...conversations].sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
      )[0];
      if (!first) throw new Error('Missing project summary');
      const conversation = await aiChatApi.getConversation(first.id);
      const message = conversation.messages.find(
        (entry) => entry.role === 'assistant' && entry.content.trim(),
      );
      if (!message) throw new Error('Missing project summary');
      if (active) setSummary(message.content);
    };
    void load().catch(() => {
      if (active) setFailed(true);
    });
    return () => {
      active = false;
    };
  }, [projectId, attempt]);
  if (summary)
    return (
      <Box className={classes.summary}>
        <ChatMarkdown content={summary} />
      </Box>
    );
  return (
    <Box>
      <Typography className={classes.detail}>
        {failed
          ? 'Could not load the project summary.'
          : 'Loading project summary…'}
      </Typography>
      {failed && (
        <Box className={classes.actions}>
          <Button
            size="small"
            onClick={() => setAttempt((current) => current + 1)}
          >
            Try again
          </Button>
        </Box>
      )}
    </Box>
  );
};

/** Current import status and recovery actions, without a character or transient bubbles. */
export const ImportCard: React.FC<ImportCardProps> = ({
  row,
  fileCount = 0,
  startedAt,
  uploadFailure = null,
  busy = false,
  onRetry,
  onDiscard,
  onOpen,
}) => {
  const { classes } = useStyles();
  const elapsed = useElapsed(
    row?.createdAt ?? startedAt ?? new Date().toISOString(),
  );
  const count = fileCount || row?.fileCount || 0;
  const files = `${count} ${count === 1 ? 'file' : 'files'}`;
  const step = row?.step ?? null;
  const failed = uploadFailure !== null || row?.failed === true;
  const retryable = uploadFailure?.retryable ?? row?.retryable ?? false;
  let title = 'Reading files…';
  let detail = files;
  if (busy) title = 'Trying again…';
  else if (uploadFailure) {
    title = 'Could not upload the files';
    detail = uploadFailure.text;
  } else if (!row) {
    title = 'Uploading files…';
    detail = `${files} · ${elapsed}`;
  } else if (row.projectId !== null) {
    title = 'Project created';
    detail = '';
  } else if (row.failed) {
    title = 'Could not create the project';
    detail = row.failureText ?? '';
  } else if (step) {
    if (step.index === step.count) title = 'Creating the project…';
    detail = `Step ${step.index} of ${step.count}: ${step.text.toLowerCase()} · ${elapsed} · usually 5–25 min`;
  }
  return (
    <Box className={classes.status} role="status">
      <Typography className={classes.statusTitle}>{title}</Typography>
      {detail && <Typography className={classes.detail}>{detail}</Typography>}
      {row?.projectId != null && (
        <FfuProjectSummary projectId={row.projectId} />
      )}
      {row?.projectId != null && onOpen && (
        <Box className={classes.actions}>
          <Button size="small" variant="contained" onClick={onOpen}>
            Open project
          </Button>
        </Box>
      )}
      {failed && !busy && (
        <Box className={classes.actions}>
          {retryable && onRetry && (
            <Button size="small" variant="contained" onClick={onRetry}>
              Try again
            </Button>
          )}
          {onDiscard && (
            <Button size="small" onClick={onDiscard}>
              Discard
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

/** File-picker entry to the same FFU flow as page-wide drops. */
export const DropCard: React.FC<{
  onFiles: (uploads: ProjectFileClientUpload[]) => void;
  error: string | null;
}> = ({ onFiles, error }) => {
  const { classes } = useStyles();
  const input = useRef<HTMLInputElement>(null);
  return (
    <Box
      className={classes.drop}
      role="button"
      tabIndex={0}
      onClick={() => input.current?.click()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          input.current?.click();
        }
      }}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes('Files')) event.preventDefault();
      }}
      onDrop={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        event.stopPropagation();
        void collectProjectFilesFromDataTransfer(event.dataTransfer).then(
          onFiles,
        );
      }}
    >
      <Typography className={classes.statusTitle}>
        Drop project documents here
      </Typography>
      <Typography className={classes.detail}>
        {error ??
          'A new project is created from the documents. Or click to choose files'}
      </Typography>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          const uploads = collectProjectFilesFromFileList(
            event.target.files ?? [],
          );
          event.target.value = '';
          onFiles(uploads);
        }}
      />
    </Box>
  );
};

export type FfuChatEntry = ImportCardProps & { key: string };

/** FFU progress in the same floating window and assistant-message components as the project agent. */
export const FfuImportChat: React.FC<{
  entries: FfuChatEntry[];
  error?: string | null;
  onClose: () => void;
}> = ({ entries, error, onClose }) => {
  const { classes } = useStyles();
  const [size, setSize] = useLocalStorage(
    CHAT_SIZE_STORAGE_KEY,
    DEFAULT_CHAT_SIZE,
  );
  const content = useRef<HTMLDivElement>(null);
  const follow = useRef(true);
  const latest = entries
    .map(
      (entry) =>
        `${entry.key}:${entry.row?.messages.length ?? 0}:${entry.row?.state}:${entry.uploadFailure?.text}`,
    )
    .join('|');
  useLayoutEffect(() => {
    if (follow.current && content.current)
      content.current.scrollTop = content.current.scrollHeight;
  }, [latest, error]);
  return (
    <Portal>
      <ChatWindow
        className={classes.chat}
        width={size.width}
        height={size.height}
        onResize={setSize}
      >
        <ChatWindowHeader>
          <Typography className={classes.title}>
            Create a project with AI
          </Typography>
          <IconButton
            className={classes.close}
            size="small"
            aria-label="Close import chat"
            onClick={onClose}
          >
            <Close fontSize="small" />
          </IconButton>
        </ChatWindowHeader>
        <ChatWindowContent
          scrollRef={content}
          onScroll={(event) => {
            const node = event.currentTarget;
            follow.current =
              node.scrollHeight - node.scrollTop - node.clientHeight < 32;
          }}
        >
          {entries.map(({ key, ...entry }) => (
            <React.Fragment key={key}>
              {(entry.row?.messages ?? []).map((message, index, messages) => (
                <AgentChatMessage
                  key={message.id}
                  message={{ content: message.text, createdAt: message.at }}
                  timestamp={
                    shouldShowChatTimestamp(message.at, messages[index - 1]?.at)
                      ? formatChatThreadTimestamp(message.at, new Date())
                      : undefined
                  }
                />
              ))}
              <ImportCard {...entry} />
            </React.Fragment>
          ))}
          {error && (
            <AgentChatMessage
              message={{ content: error, createdAt: new Date().toISOString() }}
            />
          )}
        </ChatWindowContent>
      </ChatWindow>
    </Portal>
  );
};

/** An import's card: an upload, a run, a failure or a hand-over. */
type Slot = {
  /** React key, the same for the card's whole life. */
  key: string;
  batchId: string | null;
  /** Kept until the server has them, so a failed upload can be sent again. */
  uploads: ProjectFileClientUpload[] | null;
  progress: FfuImportProgress | null;
  fileCount: number;
  startedAt: string;
  uploadFailure: UploadFailure | null;
  busy: boolean;
};

let keys = 0;
const newKey = () => `ffu-card-${++keys}`;

/**
 * Admin-only FFU uploads and progress. The project list uses a floating chat;
 * other views retain progress cards in the menu. Keep mounted while the menu
 * is closed so uploads continue and completed transcripts remain available.
 */
export const FfuImportCards: React.FC<{
  enablePageDrop?: boolean;
}> = ({ enablePageDrop = false }) => {
  const { imports, refresh } = useFfuImports();
  const navigate = useNavigate();
  const organization = useSelectedOrganizationName();
  const { fetchProjects } = useProjectState('fetchProjects');
  const [error, setError] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [chatOpen, setChatOpen] = useState(true);
  const pendingImports = useRef(new Set<string>());
  // Batches discarded or replaced by a retry. Completed transcripts remain visible.
  const released = useRef(new Set<string>());

  const update = useCallback((key: string, change: Partial<Slot>) => {
    setSlots((current) =>
      current.map((slot) => (slot.key === key ? { ...slot, ...change } : slot)),
    );
  }, []);

  const remove = useCallback((key: string) => {
    setSlots((current) => current.filter((slot) => slot.key !== key));
  }, []);

  // Imports started before a reload, or in another tab, get a card too,
  // unless they already have their project.
  useEffect(() => {
    setSlots((current) => {
      const known = new Set(current.map((slot) => slot.batchId));
      const adopted = imports
        .filter(
          (row) =>
            !known.has(row.batchId) &&
            !released.current.has(row.batchId) &&
            row.projectId === null,
        )
        .map((row): Slot => ({
          key: row.batchId,
          batchId: row.batchId,
          uploads: null,
          progress: row,
          fileCount: row.fileCount,
          startedAt: row.createdAt,
          uploadFailure: null,
          busy: false,
        }));
      const retained = current.map((slot) => {
        const progress =
          imports.find((row) => row.batchId === slot.batchId) ?? slot.progress;
        return progress === slot.progress ? slot : { ...slot, progress };
      });
      return [...retained, ...adopted];
    });
    for (const row of imports) {
      if (row.projectId === null) {
        pendingImports.current.add(row.batchId);
        continue;
      }
      // A finished row on mount belongs to an earlier run. Refetching it can
      // unmount this menu behind Design's loader and restart the same cycle.
      if (pendingImports.current.delete(row.batchId)) void fetchProjects();
    }
  }, [imports, fetchProjects]);

  const upload = useCallback(
    (key: string, uploads: ProjectFileClientUpload[]) => {
      update(key, { uploadFailure: null, busy: false });
      ffuImportApi
        .start(uploads)
        .then((batchId) => {
          pendingImports.current.add(batchId);
          update(key, { batchId, uploads: null });
          void refresh();
        })
        .catch((reason: unknown) =>
          update(key, { uploadFailure: uploadFailureOf(reason) }),
        );
    },
    [refresh, update],
  );

  const onFiles = (uploads: ProjectFileClientUpload[]) => {
    setChatOpen(true);
    if (uploads.length === 0) {
      setError('No files to read');
      return;
    }
    setError(null);
    const key = newKey();
    setSlots((current) => [
      {
        key,
        batchId: null,
        uploads,
        progress: null,
        fileCount: uploads.length,
        startedAt: new Date().toISOString(),
        uploadFailure: null,
        busy: false,
      },
      ...current,
    ]);
    upload(key, uploads);
  };

  const dragging = useFfuFileDrop({
    enabled: enablePageDrop,
    onFiles,
    onError: () => {
      setError('Could not read the files');
      setChatOpen(true);
    },
  });

  const retry = (slot: Slot) => {
    if (slot.batchId === null) {
      if (slot.uploads !== null) upload(slot.key, slot.uploads);
      return;
    }
    const previous = slot.batchId;
    update(slot.key, { busy: true });
    ffuImportApi
      .retry(previous)
      .then((row) => {
        pendingImports.current.add(row.batchId);
        if (row.batchId !== previous) released.current.add(previous);
        update(slot.key, { batchId: row.batchId, progress: row, busy: false });
        void refresh();
      })
      .catch(() => update(slot.key, { busy: false }));
  };

  const openProject = async (projectId: number) => {
    try {
      const projects = await fetchProjects();
      const project = projects[projectId];
      navigate(
        requireCreatedProjectDesignPath(
          organization,
          projectId,
          project?.active_version_id ?? project?.versionIds[0],
        ),
      );
    } catch {
      setError('Could not open the project. Try again.');
    }
  };

  const discard = (slot: Slot) => {
    if (slot.batchId !== null) {
      released.current.add(slot.batchId);
      void ffuImportApi.abandon(slot.batchId).catch(() => undefined);
    }
    remove(slot.key);
  };

  const entries = [...slots].reverse().map((slot) => {
    const projectId = slot.progress?.projectId;
    return {
      key: slot.key,
      row: slot.progress,
      fileCount: slot.fileCount,
      startedAt: slot.startedAt,
      uploadFailure: slot.uploadFailure,
      busy: slot.busy,
      onRetry: () => retry(slot),
      onDiscard: () => discard(slot),
      onOpen: projectId != null ? () => void openProject(projectId) : undefined,
    };
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <DropZoneOverlay
        state={
          dragging ? { kind: 'dragging', validity: 'valid' } : { kind: 'idle' }
        }
        labels={{
          neutralTitle: 'Drop files to upload',
          neutralSubtitle: 'A new project will be created using AI',
        }}
      />
      <DropCard onFiles={onFiles} error={error} />
      {!enablePageDrop &&
        entries.map(({ key, ...entry }) => <ImportCard key={key} {...entry} />)}
      {enablePageDrop && slots.length > 0 && (
        <Button size="small" onClick={() => setChatOpen(true)}>
          View imports
        </Button>
      )}
      {enablePageDrop && chatOpen && (slots.length > 0 || error !== null) && (
        <FfuImportChat
          entries={entries}
          error={error}
          onClose={() => setChatOpen(false)}
        />
      )}
    </Box>
  );
};
